/**
 * TRANSLITTÉRATION DES NOMS ET DES ADRESSES.
 *
 * Pourquoi elle existe : le générateur de PDF écrit en « helvetica », l'une des
 * 14 polices standard de jsPDF, déclarée « /WinAnsiEncoding » et sans
 * « /ToUnicode ». Tout caractère hors WinAnsi y part sur DEUX octets UTF-16 que
 * le lecteur relit comme DEUX caractères WinAnsi : « вул. » s'imprimait
 * « 2C;. ». Le repli à la ligne était faux par surcroît, « splitTextToSize »
 * mesurant la largeur de ces faux glyphes.
 *
 * RÈGLE ABSOLUE : ON NE SUPPRIME JAMAIS UN CARACTÈRE. Une version antérieure
 * retirait ce qu'elle ne savait pas romaniser — un nom arabe devenait vide, une
 * adresse chinoise se réduisait à son numéro. Une valeur vide se propage en
 * silence : elle passe les « filter(Boolean) », elle ne déclenche aucune alerte,
 * et personne ne s'aperçoit que la facture est partie sans destinataire lisible.
 * Ici, à défaut de romanisation, c'est l'ORIGINAL qui ressort, accompagné du
 * verdict qui dit au PDF de changer de police.
 *
 * Les données en base ne sont jamais modifiées par ce module : les champs
 * d'origine restent tels qu'ils ont été saisis ou reçus de Stripe. Ce sont les
 * actions client qui rangent le résultat dans des colonnes PARALLÈLES.
 */

import anyAscii from "any-ascii";

// Le nom du pays vit dans son propre module : les écrans en ont besoin, des
// tables d'« any-ascii » non. Réexporté ici, où les appelants le cherchent.
export { nomDuPays } from "./pays";
import { nomDuPays } from "./pays";

// ---------------------------------------------------------------------------
// 1. Ce que la police par défaut du PDF sait écrire
// ---------------------------------------------------------------------------

/**
 * Les caractères que WinAnsi place entre 0x80 et 0x9F, là où Latin-1 n'a que
 * des codes de contrôle. Les cinq positions inutilisées de cette plage
 * (0x81, 0x8D, 0x8F, 0x90, 0x9D) n'y figurent pas.
 */
const WINANSI_HAUT = "€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ";

function estRepresentable(caractere: string): boolean {
    const point = caractere.codePointAt(0) ?? 0;
    if (point === 0x09 || point === 0x0a || point === 0x0d) return true;
    if (point >= 0x20 && point <= 0x7e) return true;
    if (point >= 0xa0 && point <= 0xff) return true;
    return WINANSI_HAUT.includes(caractere);
}

/** Vrai si la police par défaut du PDF peut imprimer ce texte tel quel. */
export function estEcrivableTelQuel(texte: string): boolean {
    return [...texte].every(estRepresentable);
}

// ---------------------------------------------------------------------------
// 2. Reconnaître l'écriture
// ---------------------------------------------------------------------------

export type Script =
    | "latin"
    | "cyrillique"
    | "grec"
    | "arabe"
    | "hebreu"
    | "han"
    | "kana"
    | "hangul"
    | "thai"
    | "autre";

const PLAGES: ReadonlyArray<readonly [Script, RegExp]> = [
    ["cyrillique", /[Ѐ-ӿԀ-ԯ]/],
    ["grec", /[Ͱ-Ͽἀ-῿]/],
    ["arabe", /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/],
    ["hebreu", /[֐-׿יִ-ﭏ]/],
    // Les kanas AVANT les idéogrammes : un texte japonais mêle les deux, et
    // c'est la présence de kanas qui le distingue du chinois. L'ordre est la
    // seule chose qui permette de le voir — voir « qualiteDuScript ».
    ["kana", /[぀-ヿㇰ-ㇿ]/],
    ["hangul", /[가-힯ᄀ-ᇿ㄰-㆏]/],
    ["han", /[一-鿿㐀-䶿豈-﫿]/],
    ["thai", /[฀-๿]/],
];

/**
 * L'écriture dominante d'un texte. « latin » signifie « rien à translittérer »,
 * pas « uniquement des lettres A-Z » : les accents français en font partie.
 */
export function detecterScript(texte: string): Script {
    if (estEcrivableTelQuel(texte)) return "latin";
    for (const [script, plage] of PLAGES) {
        if (plage.test(texte)) return script;
    }
    // Latin étendu : « Bucureşti », « Łódź », « Kaunas Ąžuolynas ». WinAnsi ne
    // porte pas ces diacritiques, mais retirer l'accent redonne la lettre juste
    // — la romanisation est alors DÉTERMINISTE, pas approchée.
    if (estEcrivableTelQuel(texte.normalize("NFD").replace(/\p{M}/gu, ""))) return "latin";
    return "autre";
}

/** Les écritures qui se lisent de droite à gauche. */
export function estDroiteAGauche(script: Script): boolean {
    return script === "arabe" || script === "hebreu";
}

/**
 * Les écritures que « public/fonts/DejaVuSans.ttf » sait dessiner. DejaVu ne
 * porte AUCUN idéogramme : ni han, ni kana, ni hangul. C'est un constat sur le
 * fichier embarqué, pas une préférence — voir « src/lib/pdf-polices.ts ».
 */
export function couvertParLaPoliceUnicode(script: Script): boolean {
    return script === "latin" || script === "cyrillique" || script === "grec"
        || script === "arabe" || script === "hebreu" || script === "thai";
}

// ---------------------------------------------------------------------------
// 3. Les libellés d'adresse
// ---------------------------------------------------------------------------

/**
 * Une adresse ne se translittère pas seulement : elle se TRADUIT. « вул. »
 * devenu « vul. » ne dit rien à un lecteur français, alors que « Rue » se
 * comprend. Les clés sont en minuscules, avec le point de l'abréviation quand
 * il en fait partie.
 *
 * Les lettres seules et ambiguës ne sont retenues qu'abrégées — « д. » oui,
 * « д » non : la seconde forme dévorerait des mots entiers.
 *
 * La casse du remplacement est délibérée : le type de voie ouvre la ligne et
 * prend la majuscule, le complément — appartement, étage, bureau — la suit et
 * n'en prend pas. C'est l'usage français.
 */
const LIBELLES_ADRESSE = new Map<string, string>([
    // Types de voie — ukrainien et russe
    ["вул.", "Rue"], ["вулиця", "Rue"], ["ул.", "Rue"], ["улица", "Rue"],
    ["просп.", "Avenue"], ["проспект", "Avenue"], ["пр-т", "Avenue"], ["пр.", "Avenue"],
    ["бул.", "Boulevard"], ["бульв.", "Boulevard"], ["бульвар", "Boulevard"],
    ["пров.", "Ruelle"], ["провулок", "Ruelle"], ["пер.", "Ruelle"], ["переулок", "Ruelle"],
    ["пл.", "Place"], ["площа", "Place"], ["площадь", "Place"],
    ["наб.", "Quai"], ["набережна", "Quai"], ["набережная", "Quai"],
    ["ш.", "Route"], ["шосе", "Route"], ["шоссе", "Route"],
    ["туп.", "Impasse"], ["тупик", "Impasse"],
    // Compléments
    ["кв.", "appartement"], ["квартира", "appartement"],
    ["буд.", "n°"], ["будинок", "n°"], ["д.", "n°"], ["дом", "n°"],
    ["корп.", "bâtiment"], ["корпус", "bâtiment"],
    ["під'їзд", "entrée"], ["подъезд", "entrée"],
    ["пов.", "étage"], ["поверх", "étage"], ["эт.", "étage"], ["этаж", "étage"],
    ["оф.", "bureau"], ["офіс", "bureau"], ["офис", "bureau"],
    // Découpage administratif
    ["обл.", "région"], ["область", "région"],
    ["р-н", "district"], ["район", "district"],
    ["с.", "village"], ["село", "village"], ["смт", "commune"],
    ["а/с", "BP"], ["а/я", "BP"],
    // Grec
    ["οδός", "Rue"], ["λεωφόρος", "Avenue"], ["πλατεία", "Place"],
]);

// ---------------------------------------------------------------------------
// 4. Les tables normées
// ---------------------------------------------------------------------------

type Table = Record<string, string | [string, string]>;

/**
 * Ukrainien, norme officielle KMU 2010 (résolution n° 55 du 27 janvier 2010) —
 * celle qui figure sur les passeports ukrainiens. Cinq lettres ont deux
 * formes : la première en TÊTE DE MOT, la seconde ailleurs. « ю » donne donc
 * « yu- » à l'initiale et « -iu » au milieu, d'où « Ілюши » → « Iliushy ».
 *
 * Cette table existe malgré « any-ascii », qui saurait romaniser du cyrillique :
 * elle est simplement PLUS JUSTE ici. Sur « вул. Ілюши Кулика », any-ascii rend
 * « vul. Ilyushi Kulika » — une romanisation russe, sur une adresse ukrainienne,
 * et sans traduire le libellé.
 */
const UKRAINIEN: Table = {
    а: "a", б: "b", в: "v", г: "h", ґ: "g", д: "d", е: "e",
    є: ["ye", "ie"], ж: "zh", з: "z", и: "y", і: "i", ї: ["yi", "i"],
    й: ["y", "i"], к: "k", л: "l", м: "m", н: "n", о: "o", п: "p",
    р: "r", с: "s", т: "t", у: "u", ф: "f", х: "kh", ц: "ts",
    ч: "ch", ш: "sh", щ: "shch", ь: "", ю: ["yu", "iu"], я: ["ya", "ia"],
};

/**
 * Russe, BGN/PCGN. Les lettres communes y sont volontairement différentes de
 * l'ukrainien : « г » vaut « g » et non « h », « и » vaut « i » et non « y ».
 */
const RUSSE: Table = {
    а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "yo", ж: "zh",
    з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o",
    п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "kh", ц: "ts",
    ч: "ch", ш: "sh", щ: "shch", ъ: "", ы: "y", ь: "", э: "e",
    ю: "yu", я: "ya",
};

/** Grec moderne, translittération d'usage. */
const GREC: Table = {
    α: "a", β: "v", γ: "g", δ: "d", ε: "e", ζ: "z", η: "i", θ: "th",
    ι: "i", κ: "k", λ: "l", μ: "m", ν: "n", ξ: "x", ο: "o", π: "p",
    ρ: "r", σ: "s", ς: "s", τ: "t", υ: "y", φ: "f", χ: "ch", ψ: "ps",
    ω: "o", ά: "a", έ: "e", ή: "i", ί: "i", ό: "o", ύ: "y", ώ: "o",
    ϊ: "i", ϋ: "y", ΐ: "i", ΰ: "y",
};

/**
 * Libellés d'adresse qui n'existent QU'EN RUSSE — l'ukrainien dit « вул. »,
 * « провулок », « будинок », « поверх ». Ils servent d'indice de langue quand
 * aucune lettre ne tranche : « ул. Пушкина » ne porte ni « і » ni « ы », mais
 * son premier mot suffit à savoir qu'on lit du russe.
 */
const LIBELLES_RUSSES = new Set([
    "ул.", "улица", "переулок", "пер.", "площадь", "набережная", "шоссе",
    "квартира", "дом", "подъезд", "этаж", "эт.", "офис",
]);

/**
 * Laquelle des deux tables cyrilliques appliquer.
 *
 * Aucune fiche client ne porte de langue : elle se déduit du texte. « і ї є ґ »
 * n'existent qu'en ukrainien, « ы э ъ ё » qu'en russe ; à défaut de lettre
 * décisive, un libellé d'adresse propre au russe tranche.
 *
 * Sans aucun de ces indices, on retient l'ukrainien : c'est la seule langue
 * cyrillique rencontrée dans les données, et l'écart porte alors sur « г » et
 * « и », soit l'orthographe du romanisé, jamais l'exactitude de l'adresse.
 */
function tableCyrillique(texte: string): Table {
    if (/[іїєґ]/i.test(texte)) return UKRAINIEN;
    if (/[ыэъё]/i.test(texte)) return RUSSE;

    const mots = texte.toLowerCase().match(MOT) ?? [];
    if (mots.some((mot) => LIBELLES_RUSSES.has(mot))) return RUSSE;

    return UKRAINIEN;
}

// ---------------------------------------------------------------------------
// 5. La translittération par table
// ---------------------------------------------------------------------------

/** Signe mou, signe dur et apostrophe dure : aucun son propre, aucune lettre. */
const MUETS = new Set(["'", "’", "ʼ"]);

function translittererMot(mot: string, table: Table): string {
    // « ТОВ » doit donner « TOV », et non « TOv » : un mot entièrement
    // capitalisé le reste. La comparaison avec la minuscule écarte les suites
    // sans casse — « 131 » n'est pas une majuscule.
    const touteMajuscule =
        mot.length > 1 && mot === mot.toUpperCase() && mot !== mot.toLowerCase();

    const minuscules = mot.toLowerCase();
    let resultat = "";
    let enTete = true;
    let i = 0;

    while (i < minuscules.length) {
        // « зг » vaut « zgh » en ukrainien, et non « zh » — sans quoi
        // « Згорани » et « Жорани » se confondraient. Seul digramme de la norme.
        const digramme =
            table === UKRAINIEN && minuscules.startsWith("зг", i) ? "zgh" : null;
        const longueur = digramme ? 2 : 1;

        const lettre = minuscules[i];
        const valeur = table[lettre];

        let latin: string;
        if (digramme) {
            latin = digramme;
        } else if (valeur === undefined) {
            latin = MUETS.has(lettre) ? "" : mot[i];
        } else if (Array.isArray(valeur)) {
            latin = enTete ? valeur[0] : valeur[1];
        } else {
            latin = valeur;
        }

        if (latin !== "") enTete = false;

        // La casse suit celle de la lettre d'origine, jamais celle de la
        // table : « Х » donne « Kh » et non « KH ».
        if (touteMajuscule) {
            resultat += latin.toUpperCase();
        } else if (mot[i] !== lettre) {
            resultat += latin.charAt(0).toUpperCase() + latin.slice(1);
        } else {
            resultat += latin;
        }

        i += longueur;
    }

    return resultat;
}

/**
 * Découpe en « mots » au sens large : lettres, chiffres et apostrophes, plus le
 * point final d'une abréviation et la barre de « а/с ». Tout le reste —
 * espaces, virgules, tirets — est rendu tel quel.
 *
 * Le découpage est indispensable : les libellés se cherchent par mot entier, et
 * la règle de tête de mot de la norme ukrainienne n'a de sens qu'ici. Il évite
 * aussi toute lookbehind, que Safari ne connaît que depuis la version 16.4 —
 * et une expression régulière refusée à l'analyse ferait échouer le PDF entier,
 * pas seulement les adresses cyrilliques.
 */
const MOT = /[\p{L}\p{N}'’]+(?:\/[\p{L}\p{N}]+)?\.?/gu;

const TABLES_NORMEES: Partial<Record<Script, (texte: string) => Table>> = {
    cyrillique: tableCyrillique,
    grec: () => GREC,
};

// ---------------------------------------------------------------------------
// 6. Le verdict
// ---------------------------------------------------------------------------

/**
 * Ce que vaut la romanisation obtenue.
 *
 *  - « exacte »     : rien à translittérer, ou une norme officielle appliquée
 *                     (KMU 2010 pour l'ukrainien, BGN/PCGN pour le russe).
 *  - « approchee »  : « any-ascii » a rendu quelque chose de lisible, mais ce
 *                     n'est pas une norme et cela peut se discuter.
 *  - « impossible » : rien d'exploitable n'en est sorti. « latin » porte alors
 *                     l'ORIGINAL, jamais une chaîne vide.
 */
export type Qualite = "exacte" | "approchee" | "impossible";

export interface Translitteration {
    /** La version latine. JAMAIS vide si la source ne l'était pas. */
    latin: string;
    /** Le texte tel qu'il a été saisi ou reçu, intact. */
    original: string;
    qualite: Qualite;
    script: Script;
    /** L'original se lit de droite à gauche : le PDF doit réordonner. */
    rtl: boolean;
}

/**
 * Le japonais est le cas où « any-ascii » se trompe VRAIMENT, et non
 * seulement approximativement : il lit les kanjis à la chinoise. Vérifié —
 * « 東京都渋谷区 » ressort « DongJingDouSeGuQu » là où il faudrait
 * « Tokyo-to Shibuya-ku ». Un texte japonais est donc toujours « approchee »,
 * jamais mieux, et le commentaire est là pour que personne ne le promeuve.
 */
const SCRIPTS_APPROCHES: ReadonlySet<Script> = new Set<Script>([
    "arabe", "hebreu", "han", "kana", "hangul", "thai", "autre",
]);

// ---------------------------------------------------------------------------
// 7. L'entrée principale
// ---------------------------------------------------------------------------

/**
 * Romanise un nom ou une adresse, et dit ce que vaut le résultat.
 *
 * Un texte déjà écrivable ressort INCHANGÉ — accents français, « € », « Œ » et
 * ponctuation typographique compris. Rien n'est translittéré sans nécessité.
 */
export function translitterer(texte?: string | null): Translitteration {
    const original = (texte ?? "").normalize("NFC");

    if (!original.trim()) {
        return { latin: original, original, qualite: "exacte", script: "latin", rtl: false };
    }

    if (estEcrivableTelQuel(original)) {
        return { latin: original, original, qualite: "exacte", script: "latin", rtl: false };
    }

    const script = detecterScript(original);
    const rtl = estDroiteAGauche(script);
    const fabriqueTable = TABLES_NORMEES[script];
    const table = fabriqueTable ? fabriqueTable(original) : null;

    const traduit = original.replace(MOT, (mot) => {
        // Les trois apostrophes sont ramenées à la droite avant la recherche :
        // « під’їзд » saisi au clavier ukrainien et « під'їзд » recopié d'un
        // formulaire doivent trouver la même entrée.
        const cle = mot.toLowerCase().replace(/[’ʼ]/g, "'");
        const libelle = LIBELLES_ADRESSE.get(cle);
        if (libelle !== undefined) return libelle;

        // Un mot déjà latin ne passe par aucune table : « 131-Б » garde son
        // « 131 », et un nom de rue écrit en français reste intact.
        if (estEcrivableTelQuel(mot)) return mot;

        // Une table normée existe pour cette écriture : elle prime sur
        // « any-ascii », qui ignore les normes nationales.
        if (table) return translittererMot(mot, table);

        return anyAscii(mot);
    });

    // Ce qui n'a toujours pas de glyphe — un mot d'une écriture sans table
    // normée que « any-ascii » n'aurait pas couvert — passe une seconde fois,
    // caractère par caractère cette fois. Les diacritiques latins que WinAnsi
    // ignore (« ā », « ș », « ł ») se replient sur leur lettre de base.
    const latin = [...traduit]
        .map((c) => {
            if (estRepresentable(c)) return c;
            const deplie = c.normalize("NFD").replace(/\p{M}/gu, "");
            if (deplie && estEcrivableTelQuel(deplie)) return deplie;
            return anyAscii(c);
        })
        .join("")
        .replace(/[ \t]{2,}/g, " ")
        .trim();

    // LE POINT CRITIQUE. Si rien d'imprimable n'est sorti, on rend
    // l'ORIGINAL — le PDF changera de police plutôt que d'imprimer du vide.
    if (!latin || !/[\p{L}\p{N}]/u.test(latin) || !estEcrivableTelQuel(latin)) {
        return { latin: original, original, qualite: "impossible", script, rtl };
    }

    const qualite: Qualite = SCRIPTS_APPROCHES.has(script) ? "approchee" : "exacte";
    return { latin, original, qualite, script, rtl };
}

/** La version latine seule. Raccourci pour les appelants qui n'ont pas besoin
 *  du verdict — le générateur de PDF, lui, en a besoin. */
export function translittererPourPdf(texte?: string | null): string {
    return translitterer(texte).latin;
}

/** Vrai si la version latine apporte quelque chose que l'original n'a pas. */
export function differeDeLOriginal(t: Translitteration): boolean {
    return t.qualite !== "impossible" && t.latin !== t.original;
}

// ---------------------------------------------------------------------------
// 8. Le pays
// ---------------------------------------------------------------------------

/** Le pays, nommé puis rendu imprimable — « UA » → « Ukraine ». */
export function paysPourPdf(valeur?: string | null): string {
    return translittererPourPdf(nomDuPays(valeur));
}
